import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_CONFIG } from '../../config/api.config';
import { environment } from '../../../environments/environment';
import { HttpService } from '../http.service';

export type DevicePlatform = 'IOS' | 'ANDROID' | 'WEB';

export type NotificationTargetPortal =
  | 'ADMIN'
  | 'CUSTOMER'
  | 'CHANNEL_PARTNER';

export interface RegisterDeviceTokenPayload {
  fcmToken: string;
  platform: DevicePlatform;
  browser?: string;
}

export interface DeactivateDeviceTokenPayload {
  fcmToken: string;
}

export interface NotificationPreferencePayload {
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  smsEnabled?: boolean;
  inAppEnabled?: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  timezone?: string;
}

export interface ListNotificationsParams {
  page?: number;
  limit?: number;
  targetPortal?: NotificationTargetPortal;
  customerId?: string;
  isRead?: boolean;
}

export interface PushNotificationItem {
  id: string;
  notificationId?: string;
  userId?: string;
  customerId?: string;
  title: string;
  body: string;
  actionUrl?: string | null;
  portal?: NotificationTargetPortal | null;
  isRead: boolean;
  readAt?: string | null;
  deliveryStatus?: string;
  createdAt: string;
  metadata?: Record<string, unknown> | null;
}

export interface PaginatedNotifications {
  data: PushNotificationItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UnreadCountResult {
  count: number;
}

/**
 * API Gateway client for push notifications, device tokens, and preferences.
 * Customer portal always scopes with `targetPortal=CUSTOMER` and `customerId`.
 */
@Injectable({ providedIn: 'root' })
export class PushNotificationApiService {
  private readonly http = inject(HttpService);
  private readonly httpClient = inject(HttpClient);

  registerDeviceToken(
    payload: RegisterDeviceTokenPayload,
  ): Observable<unknown> {
    return this.http.post(API_CONFIG.notifications.deviceToken.register, payload);
  }

  deactivateDeviceToken(
    payload: DeactivateDeviceTokenPayload,
  ): Observable<unknown> {
    // Customer HttpService.delete does not support a request body.
    return this.httpClient.delete(
      `${environment.apiUrl}${API_CONFIG.notifications.deviceToken.deactivate}`,
      { body: payload },
    );
  }

  getNotifications(
    params?: ListNotificationsParams,
  ): Observable<PaginatedNotifications> {
    const page = Math.max(1, Math.trunc(Number(params?.page ?? 1)) || 1);
    const limit = Math.min(
      100,
      Math.max(1, Math.trunc(Number(params?.limit ?? 10)) || 10),
    );

    return this.http
      .get<unknown>(API_CONFIG.notifications.list, {
        page,
        limit,
        ...(params?.targetPortal ? { targetPortal: params.targetPortal } : {}),
        ...(params?.customerId ? { customerId: params.customerId } : {}),
        ...(params?.isRead !== undefined ? { isRead: params.isRead } : {}),
      })
      .pipe(map((res) => unwrapPaginatedNotifications(res)));
  }

  getUnreadCount(params?: {
    targetPortal?: NotificationTargetPortal;
    customerId?: string;
  }): Observable<UnreadCountResult> {
    return this.http
      .get<unknown>(API_CONFIG.notifications.unreadCount, {
        ...(params?.targetPortal ? { targetPortal: params.targetPortal } : {}),
        ...(params?.customerId ? { customerId: params.customerId } : {}),
      })
      .pipe(map((res) => unwrapUnreadCount(res)));
  }

  markRead(notificationId: string): Observable<unknown> {
    return this.http.patch(API_CONFIG.notifications.markRead(notificationId), null);
  }

  markAllRead(params?: {
    targetPortal?: NotificationTargetPortal;
    customerId?: string;
  }): Observable<unknown> {
    return this.http.patch(API_CONFIG.notifications.markAllRead, null, {
      ...(params?.targetPortal ? { targetPortal: params.targetPortal } : {}),
      ...(params?.customerId ? { customerId: params.customerId } : {}),
    });
  }

  getNotificationPreferences(): Observable<unknown> {
    return this.http.get(API_CONFIG.notifications.preferences.get);
  }

  updateNotificationPreferences(
    payload: NotificationPreferencePayload,
  ): Observable<unknown> {
    return this.http.patch(API_CONFIG.notifications.preferences.update, payload);
  }
}

function unwrapPaginatedNotifications(res: unknown): PaginatedNotifications {
  const empty: PaginatedNotifications = {
    data: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  };

  if (!res || typeof res !== 'object') {
    return empty;
  }

  const root = res as Record<string, unknown>;

  let candidate: Record<string, unknown> = root;
  if ('data' in root && root['data'] && typeof root['data'] === 'object') {
    const inner = root['data'] as Record<string, unknown> | unknown[];
    if (Array.isArray(inner)) {
      return {
        data: inner as PushNotificationItem[],
        total: Number(root['total'] ?? inner.length),
        page: Number(root['page'] ?? 1),
        limit: Number(root['limit'] ?? 20),
        totalPages: Number(root['totalPages'] ?? 0),
      };
    }
    candidate = inner as Record<string, unknown>;
  }

  if (!Array.isArray(candidate['data'])) {
    return empty;
  }

  return {
    data: candidate['data'] as PushNotificationItem[],
    total: Number(candidate['total'] ?? 0),
    page: Number(candidate['page'] ?? 1),
    limit: Number(candidate['limit'] ?? 20),
    totalPages: Number(candidate['totalPages'] ?? 0),
  };
}

function unwrapUnreadCount(res: unknown): UnreadCountResult {
  if (!res || typeof res !== 'object') {
    return { count: 0 };
  }

  const root = res as Record<string, unknown>;
  if (typeof root['count'] === 'number') {
    return { count: root['count'] };
  }

  const nested = root['data'];
  if (
    nested &&
    typeof nested === 'object' &&
    typeof (nested as Record<string, unknown>)['count'] === 'number'
  ) {
    return {
      count: (nested as Record<string, unknown>)['count'] as number,
    };
  }

  return { count: 0 };
}
